import React from "react";
import { Link } from "react-router-dom";
import { StoreSection } from "../../../../redux/services/store/storePagesApi";

type Props = {
  section?: StoreSection & {
    settings_json?: any;
    items?: Array<{
      id: number | string;
      slot?: string | null;
      sort_order?: number;
      content_json?: any;
    }>;
  };
};

type CardBlock = {
  title: string;
  subtitle?: string;
  button_text?: string;
  button_link?: string;
  image?: string;
  badge?: string;
};

const fallbackCards: Record<string, CardBlock> = {
  card_1: {
    title: "Office Muse",
    subtitle: "Tailored layers and polished everyday essentials.",
    button_text: "Shop Now",
    button_link: "/shop",
    badge: "New Edit",
    image:
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=900&q=80",
  },
  card_2: {
    title: "Weekend Edit",
    subtitle: "Relaxed fits with a refined fashion mood.",
    button_text: "Discover",
    button_link: "/shop",
    badge: "Trending",
    image:
      "https://images.unsplash.com/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=900&q=80",
  },
  card_3: {
    title: "Evening Statement",
    subtitle: "Elevated pieces designed to stand out.",
    button_text: "Explore",
    button_link: "/shop",
    badge: "Style Pick",
    image:
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=900&q=80",
  },
};

const getSettings = (section?: Props["section"]) => {
  return section?.settings_json || {};
};

const getCards = (section?: Props["section"]): CardBlock[] => {
  const items = [...(section?.items || [])]
    .filter((item) => item && item.content_json)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  if (!items.length) {
    return [fallbackCards.card_1, fallbackCards.card_2, fallbackCards.card_3];
  }

  return items.slice(0, 3).map((item, index) => {
    const fallback =
      fallbackCards[item.slot || `card_${index + 1}`] || fallbackCards[`card_${index + 1}`];

    return {
      title: item.content_json?.title || fallback.title,
      subtitle: item.content_json?.subtitle || fallback.subtitle,
      button_text: item.content_json?.button_text || fallback.button_text,
      button_link: item.content_json?.button_link || fallback.button_link,
      badge: item.content_json?.badge || fallback.badge,
      image: item.content_json?.image || fallback.image,
    };
  });
};

const PortraitCard: React.FC<{ card: CardBlock }> = ({ card }) => {
  return (
    <div
      className="fashion-editorial-portrait-card position-relative overflow-hidden rounded-4 h-100"
      style={{
        minHeight: 620,
        backgroundImage: `url(${card.image})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
      }}
    >
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.20) 40%, rgba(0,0,0,0.72) 100%)",
        }}
      />

      <div className="position-relative h-100 d-flex flex-column justify-content-end p-4 p-md-4">
        {card.badge ? (
          <div className="mb-3">
            <span
              className="d-inline-block rounded-pill px-3 py-2"
              style={{
                background: "rgba(255,255,255,0.18)",
                backdropFilter: "blur(6px)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              {card.badge}
            </span>
          </div>
        ) : null}

        <h3
          className="text-white mb-2"
          style={{
            fontSize: "clamp(24px, 2.2vw, 34px)",
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {card.title}
        </h3>

        {card.subtitle ? (
          <p
            className="text-white mb-3"
            style={{
              fontSize: 14,
              lineHeight: 1.7,
              opacity: 0.92,
              maxWidth: 280,
            }}
          >
            {card.subtitle}
          </p>
        ) : null}

        <div>
          <Link
            to={card.button_link || "/shop"}
            className="btn rounded-pill px-4 py-2"
            style={{
              background: "#ffffff",
              color: "#111111",
              border: "none",
              fontWeight: 600,
            }}
          >
            {card.button_text || "Shop Now"}
          </Link>
        </div>
      </div>
    </div>
  );
};

const FashionEditorialPortraitCardsSection: React.FC<Props> = ({ section }) => {
  const settings = getSettings(section);
  const cards = getCards(section);

  const sectionTitle = settings?.section_title || "Curated For You";
  const sectionSubtitle =
    settings?.section_subtitle ||
    "Portrait-first editorial cards designed for modern fashion storytelling.";

  return (
    <section
      className="fashion-editorial-portrait-cards-section py-5"
      style={{ background: "#ffffff" }}
    >
      <div className="container">
        <div className="text-center mb-4 mb-md-5">
          <p
            className="mb-2 text-uppercase"
            style={{
              fontSize: 12,
              letterSpacing: "2px",
              color: "#8a8a8a",
              fontWeight: 600,
            }}
          >
            Fashion Story
          </p>

          <h2
            className="mb-2"
            style={{
              fontSize: "clamp(28px, 4vw, 42px)",
              fontWeight: 700,
              color: "#111111",
            }}
          >
            {sectionTitle}
          </h2>

          <p
            className="mx-auto mb-0"
            style={{
              maxWidth: 680,
              color: "#666666",
              fontSize: 15,
              lineHeight: 1.7,
            }}
          >
            {sectionSubtitle}
          </p>
        </div>

        <div className="row g-4">
          {cards.map((card, index) => (
            <div className="col-12 col-md-6 col-lg-4" key={index}>
              <PortraitCard card={card} />
            </div>
          ))}
        </div>
      </div>

      <style>
        {`
          .fashion-editorial-portrait-card {
            box-shadow: 0 12px 30px rgba(0,0,0,0.10);
            transition: transform 0.35s ease, box-shadow 0.35s ease;
          }

          .fashion-editorial-portrait-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 20px 44px rgba(0,0,0,0.16);
          }

          @media (max-width: 991.98px) {
            .fashion-editorial-portrait-card {
              min-height: 540px !important;
            }
          }

          @media (max-width: 575.98px) {
            .fashion-editorial-portrait-card {
              min-height: 460px !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default FashionEditorialPortraitCardsSection;